import type { IWidget } from "app/components/widget/widget-types/Widget";
import type { IDataFetcher, IDataFetcherFactory } from "app/components/widget/widget-fetchers/IDataFetcher";
import { DefaultFetchService, requestParams } from "app/services/fetchService";
import dayjs from "dayjs";
import type { IData, IDataRecord } from "app/common/keyword-research/landing-pages/landingPagesTypes";
import type { IRule } from "pro-features/components/RulesQueryBuilder/src/RulesQueryBuilderTypes";
import Sentry from "@similarweb/sentry-logger";

const SERVER_API = "/api/websiteOrganicLandingPagesV2";

class LandingPagesDataFetcher<T> implements IDataFetcher<T> {
    protected widget: IWidget<T>;

    constructor(widget) {
        this.widget = widget;
    }

    fetch(apiParams = this.widget.apiParams as any) {
        const {
            webSource,
            country,
            keys,
            pageFilter,
            Change,
            from,
            to,
            isWindow,
            includeSubDomains,
            latest,
            multiIncludeExcludeKeywords: multiIncludeExcludeKeywordsString,
            multiIncludeExcludeWebsites: multiIncludeExcludeWebsitesString,
            sourceType,
            ...params
        } = apiParams;

        const [sort] = params.orderBy?.split?.(" ") ?? ["Share"];

        const _params = {
            webSource,
            country,
            key: keys.split(",")[0],
            pageFilterJSON: pageFilter,
            from,
            to,
            isWindow,
            asc: false,
            includeSubDomains,
            latest,
            Change,
            sort,
            multiIncludeExcludeKeywords: multiIncludeExcludeKeywordsString,
            multiIncludeExcludeWebsites: multiIncludeExcludeWebsitesString,
            sourceType,
        };
        const paramsAsString = requestParams(_params);

        try {
            const multiIncludeExcludeWebsitesParsed: IRule[] = multiIncludeExcludeWebsitesString
                ? JSON.parse(multiIncludeExcludeWebsitesString)
                : [];
            const multiIncludeExcludeKeywordsParsed: IRule[] = multiIncludeExcludeKeywordsString
                ? JSON.parse(multiIncludeExcludeKeywordsString)
                : [];
            return DefaultFetchService.getInstance()
                .post(`${SERVER_API}?${paramsAsString}`, [
                    ...multiIncludeExcludeKeywordsParsed.map((rule) => ({
                        ...rule,
                        field: "keyword",
                    })),
                    ...multiIncludeExcludeWebsitesParsed.map((rule) => ({
                        ...rule,
                        field: "website",
                    })),
                ])
                .then(this.transformData(params)) as any;
        } catch (e) {
            Sentry.error("Error parsing JSON strings:", e);
        }
    }
    transformData = (apiParams) => (data: IData) => {
        return {
            ...data,
            Data: (data?.Data ?? []).map((record: IDataRecord) => {
                const shareOfDesktop = record.DesktopClicks / record.Clicks;
                const { TopKeyword, ...recordRest } = record;
                return {
                    ...recordRest,
                    WebSourceShare: shareOfDesktop,
                    SearchTerm: record.TopKeyword,
                    SiteSerpFeatures: { [apiParams.key]: record.SerpFeatures },
                    SerpScrapeDate: record.LatestKeywordSerpDate,
                    Trend: Object.entries(record?.Trend || {})
                        .map(([date, clicks]) => {
                            return { Key: date, Value: clicks };
                        })
                        .sort((a, b) => {
                            const aDate = dayjs(a.Key);
                            const bDate = dayjs(b.Key);
                            return aDate.isAfter(bDate) ? 1 : -1;
                        }),
                };
            }),
        };
    };
    destroy() {
        return undefined;
    }
}

const LandingPagesDataFetcherFactory: IDataFetcherFactory<any> = {
    create: function (widget) {
        return new LandingPagesDataFetcher<any>(widget);
    },
};

export default LandingPagesDataFetcherFactory;
